'use client';

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { fadeIn, scaleIn } from '@/lib/animations';
/**
 * QuoteSection Component
 *
 * Displays a visually styled quote block with decorative opening and closing SVG quotation marks.
 * The section is responsive and centered.
 *
 * Features:
 * - Responsive layout with padding and margin for spacing.
 * - Centered quote with decorative SVG quotation marks positioned absolutely.
 * - Stylized typography for both quote and attribution.
 *
 * @returns {JSX.Element} A visually styled quote block section.
 */
const QuoteSection: React.FC = () => {
  const reduce = useReducedMotion();

  return (
    <section className="py-20 px-6 mx-auto">
      <motion.div
        className="relative"
        initial={reduce ? undefined : 'hidden'}
        whileInView={reduce ? undefined : 'visible'}
        viewport={{ once: true, amount: 0.2 }}
        variants={scaleIn}
      >
        {/* === Opening Quote SVG (Top-Left) === */}
        <motion.div
          className="absolute -top-4 left-4 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 aspect-square"
          variants={fadeIn}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 64 64"
            fill="none"
            className="w-full h-full"
          >
            {/* Double opening quotation marks path */}
            <path
              d="M37.3335 42.6667C37.3335 37.6384 37.3335 35.1243 38.8956 33.5621C40.4578 32 42.9719 32 48.0002 32C53.0284 32 55.5426 32 57.1047 33.5621C58.6668 35.1243 58.6668 37.6384 58.6668 42.6667C58.6668 47.6949 58.6668 50.2091 57.1047 51.7712C55.5426 53.3333 53.0284 53.3333 48.0002 53.3333C42.9719 53.3333 40.4578 53.3333 38.8956 51.7712C37.3335 50.2091 37.3335 47.6949 37.3335 42.6667Z"
              stroke="#333"
              strokeWidth="5"
            />
            <path
              d="M37.3335 42.6666V31.6336C37.3335 21.8551 44.042 13.5622 53.3335 10.6666"
              stroke="#333"
              strokeWidth="5"
              strokeLinecap="round"
            />
            <path
              d="M5.3335 42.6667C5.3335 37.6384 5.3335 35.1243 6.8956 33.5621C8.45768 32 10.9718 32 16.0002 32C21.0285 32 23.5426 32 25.1047 33.5621C26.6668 35.1243 26.6668 37.6384 26.6668 42.6667C26.6668 47.6949 26.6668 50.2091 25.1047 51.7712C23.5426 53.3333 21.0285 53.3333 16.0002 53.3333C10.9718 53.3333 8.45768 53.3333 6.8956 51.7712C5.3335 50.2091 5.3335 47.6949 5.3335 42.6667Z"
              stroke="#333"
              strokeWidth="5"
            />
            <path
              d="M5.3335 42.6666V31.6336C5.3335 21.8551 12.0421 13.5622 21.3335 10.6666"
              stroke="#333"
              strokeWidth="5"
              strokeLinecap="round"
            />
          </svg>
        </motion.div>

        {/* === Quote Content === */}
        <blockquote className="text-center pt-8">
          <motion.p
            className="text-xl sm:text-2xl lg:text-[48px] lg:leading-[56px] lg:tracking-[-0.96px] font-normal font-mate text-text-primary mx-auto mb-6 sm:mb-8 lg:mb-10"
            variants={fadeIn}
          >
            Consulting isn't about giving answers. It's about unlocking clarity, enabling action,
            and accelerating change.
          </motion.p>
          <motion.footer
            className="text-lg sm:text-xl lg:text-[48px] lg:leading-[56px] lg:tracking-[-0.96px] font-normal font-mate text-text-primary max-w-xl mx-auto"
            variants={fadeIn}
          >
            — Adapted industry quote
          </motion.footer>
        </blockquote>

        {/* === Closing Quote SVG (Bottom-Right) === */}
        <motion.div
          className="absolute -bottom-10 right-4 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 aspect-square"
          variants={fadeIn}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 64 64"
            fill="none"
            className="w-full h-full"
          >
            {/* Double closing quotation marks path */}
            <path
              d="M26.6666 21.3334C26.6666 26.3617 26.6666 28.8758 25.1045 30.4379C23.5424 32.0001 21.0282 32.0001 15.9999 32.0001C10.9716 32.0001 8.45744 32.0001 6.89536 30.4379C5.33325 28.8758 5.33325 26.3617 5.33325 21.3334C5.33325 16.3051 5.33325 13.7909 6.89536 12.2289C8.45744 10.6667 10.9716 10.6667 15.9999 10.6667C21.0282 10.6667 23.5424 10.6667 25.1045 12.2289C26.6666 13.7909 26.6666 16.3051 26.6666 21.3334Z"
              stroke="#333"
              strokeWidth="5"
            />
            <path
              d="M26.6667 18.6667V30.619C26.6667 41.2126 19.9582 50.1966 10.6667 53.3334"
              stroke="#333"
              strokeWidth="5"
              strokeLinecap="round"
            />
            <path
              d="M58.6666 21.3334C58.6666 26.3617 58.6666 28.8758 57.1045 30.4379C55.5423 32.0001 53.0282 32.0001 47.9999 32.0001C42.9717 32.0001 40.4575 32.0001 38.8954 30.4379C37.3333 28.8758 37.3333 26.3617 37.3333 21.3334C37.3333 16.3051 37.3333 13.7909 38.8954 12.2289C40.4575 10.6667 42.9717 10.6667 47.9999 10.6667C53.0282 10.6667 55.5423 10.6667 57.1045 12.2289C58.6666 13.7909 58.6666 16.3051 58.6666 21.3334Z"
              stroke="#333"
              strokeWidth="5"
            />
            <path
              d="M58.6667 18.6667V30.619C58.6667 41.2126 51.9582 50.1966 42.6667 53.3334"
              stroke="#333"
              strokeWidth="5"
              strokeLinecap="round"
            />
          </svg>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default QuoteSection;
